import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import StockHeader from './components/StockHeader';
import StockChart from './components/StockChart';
import AIInsightPanel from './components/AIInsightPanel';
import NewsFeed from './components/NewsFeed';
import ChatInterface from './components/ChatInterface';
import { 
  Stock, 
  WatchlistItem, 
  ChartData, 
  TechnicalIndicators, 
  NewsItem, 
  AIInsight, 
  PriceAlert, 
  TimeRange 
} from './types';
import {
  mockStocks,
  generateMockChartData,
  createWatchlistItems,
  mockTechnicalIndicators,
  mockNews,
  mockAIInsights
} from './services/mockData';
import { stockAPI } from './services/stockAPI';

function App() {
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [selectedStock, setSelectedStock] = useState<string>('AAPL');
  const [currentStock, setCurrentStock] = useState<Stock>(mockStocks.AAPL);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [technicalIndicators, setTechnicalIndicators] = useState<TechnicalIndicators>(mockTechnicalIndicators.AAPL);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [aiInsight, setAiInsight] = useState<AIInsight>(mockAIInsights.AAPL);
  const [alerts, setAlerts] = useState<PriceAlert[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRange>('1M');

  // Initialize watchlist with default stocks and fetch real-time data
  useEffect(() => {
    const defaultSymbols = ['AAPL', 'NVDA', 'TSLA', 'MSFT', 'GOOGL'];
    
    // Initialize with mock data first for immediate display
    setWatchlist(createWatchlistItems(defaultSymbols));
    
    // Fetch real-time data
    const fetchRealTimeData = async () => {
      try {
        console.log('Fetching real-time stock data...');
        const realTimeStocks = await stockAPI.getMultipleQuotes(defaultSymbols);
        
        // Update watchlist with real data
        const updatedWatchlist = realTimeStocks.map(stock => ({
          ...stock,
          sparklineData: createWatchlistItems([stock.symbol])[0].sparklineData
        }));
        
        setWatchlist(updatedWatchlist);
        
        // Update current stock if it's in the fetched data
        const currentStockData = realTimeStocks.find(stock => stock.symbol === selectedStock);
        if (currentStockData) {
          setCurrentStock(currentStockData);
        }
        
        console.log('Real-time data updated successfully');
      } catch (error) {
        console.warn('Failed to fetch real-time data, using mock data:', error);
      }
    };
    
    // Fetch immediately
    fetchRealTimeData();
    
    // Set up periodic updates every 30 seconds
    const interval = setInterval(fetchRealTimeData, 30000);
    
    return () => clearInterval(interval);
  }, [selectedStock]);

  // Update data when selected stock changes
  useEffect(() => {
    const fetchStockData = async () => {
      try {
        // Fetch real-time data for selected stock
        const realTimeStock = await stockAPI.getStockQuote(selectedStock);
        setCurrentStock(realTimeStock);
        
        // Fetch historical data for charts
        const rangeMap: Record<TimeRange, string> = {
          '1D': '1d',
          '1W': '5d', 
          '1M': '1mo',
          '3M': '3mo',
          '1Y': '1y',
          '5Y': '5y'
        };
        
        const historicalData = await stockAPI.getHistoricalData(selectedStock, rangeMap[timeRange]);
        setChartData(historicalData);
        
        console.log(`Updated ${selectedStock} with real-time data:`, realTimeStock);
      } catch (error) {
        console.warn(`Failed to fetch real-time data for ${selectedStock}, using mock data:`, error);
        // Fallback to mock data
        if (mockStocks[selectedStock]) {
          setCurrentStock(mockStocks[selectedStock]);
          setChartData(generateMockChartData(selectedStock, getTimeRangeDays(timeRange)));
        }
      }
      
      // These still use mock data for now
      setTechnicalIndicators(mockTechnicalIndicators[selectedStock] || mockTechnicalIndicators.AAPL);
      setNews(mockNews[selectedStock] || []);
      setAiInsight(mockAIInsights[selectedStock] || mockAIInsights.AAPL);
    };
    
    fetchStockData();
  }, [selectedStock, timeRange]);

  const getTimeRangeDays = (range: TimeRange): number => {
    switch (range) {
      case '1D': return 1;
      case '1W': return 7;
      case '1M': return 30;
      case '3M': return 90;
      case '1Y': return 365;
      case '5Y': return 1825;
      default: return 30;
    }
  };

  const handleStockSelect = (symbol: string) => {
    setSelectedStock(symbol);
  };

  const handleAddStock = async (symbol: string) => {
    const existingIndex = watchlist.findIndex(item => item.symbol === symbol);
    if (existingIndex !== -1) {
      console.log(`${symbol} is already in watchlist`);
      return;
    }

    try {
      console.log(`Fetching real-time data for ${symbol}...`);
      // Fetch real-time data for the new stock
      const realTimeStock = await stockAPI.getStockQuote(symbol);
      
      // Add to watchlist with real data
      const newWatchlistItem = {
        ...realTimeStock,
        sparklineData: createWatchlistItems([symbol])[0].sparklineData
      };
      
      setWatchlist(prev => [...prev, newWatchlistItem]);
      console.log(`Added ${symbol} to watchlist with real-time data:`, realTimeStock);
    } catch (error) {
      console.warn(`Failed to fetch real-time data for ${symbol}, using fallback:`, error);
      
      // Fallback to mock data if API fails
      const fallbackStock: Stock = {
        symbol,
        name: `${symbol} Corporation`,
        price: Math.random() * 200 + 50,
        change: (Math.random() - 0.5) * 10,
        changePercent: (Math.random() - 0.5) * 5,
        volume: Math.floor(Math.random() * 50000000) + 10000000,
        marketCap: Math.random() * 1000000000000 + 100000000000,
        pe: Math.random() * 50 + 10,
        high52Week: Math.random() * 300 + 100,
        low52Week: Math.random() * 100 + 50
      };
      
      const newWatchlistItem = {
        ...fallbackStock,
        sparklineData: createWatchlistItems([symbol])[0].sparklineData
      };
      
      setWatchlist(prev => [...prev, newWatchlistItem]);
    }
  };

  const handleRemoveStock = (symbol: string) => {
    setWatchlist(prev => prev.filter(item => item.symbol !== symbol));
    if (selectedStock === symbol && watchlist.length > 1) {
      const remainingStocks = watchlist.filter(item => item.symbol !== symbol);
      if (remainingStocks.length > 0) {
        setSelectedStock(remainingStocks[0].symbol);
      }
    }
  };

  const handleCreateAlert = (alert: Omit<PriceAlert, 'id' | 'createdAt'>) => {
    const newAlert: PriceAlert = {
      ...alert,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };
    setAlerts(prev => [...prev, newAlert]);
  };

  const handleDeleteAlert = (alertId: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  };

  const handleTimeRangeChange = (range: TimeRange) => {
    setTimeRange(range);
  };

  return (
    <div className="min-h-screen bg-terminal-bg text-terminal-text">
      <div className="flex">
        {/* Sidebar */}
        <Sidebar
          watchlist={watchlist}
          selectedStock={selectedStock}
          onStockSelect={handleStockSelect}
          onAddStock={handleAddStock}
          onRemoveStock={handleRemoveStock}
        />

        {/* Main Content */}
        <div className="flex-1 min-h-screen">
          <div className="p-6 space-y-6">
            {/* Stock Header */}
            <StockHeader
              stock={currentStock}
              alerts={alerts}
              onCreateAlert={handleCreateAlert}
              onDeleteAlert={handleDeleteAlert}
            />

            {/* Chart Section */}
            <StockChart
              data={chartData}
              indicators={technicalIndicators}
              timeRange={timeRange}
              onTimeRangeChange={handleTimeRangeChange}
            />

            {/* AI Insights and News */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AIInsightPanel
                insight={aiInsight}
                symbol={selectedStock}
              />
              <NewsFeed
                news={news}
                symbol={selectedStock}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Chat Interface */}
      <ChatInterface selectedStock={selectedStock} />
    </div>
  );
}

export default App;
